import useUserPosition from '@hooks/use-user-position';
import useAutocomplete from '@mui/material/useAutocomplete';
import Input from '@components/Input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import useProvideLocations from '@hooks/use-provide-locations';
import classNames from 'classnames';
import { memo, useCallback, useEffect, useRef } from 'react';
import { formatName, hasOpened } from '@features/Location/utilities/helpers';
import useDateTime from '@hooks/use-datetime';
import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import Button from '@components/Button';
import Image from 'next/image';
import {
  Root,
  DropdownWrapper,
  Listbox,
  MarkerIcon as MarkerIconClass,
  InputHasIcon,
  SingleWash,
  LabelContainer,
  LabelButton,
  LabelIcon,
  LabelText,
} from './LocationsDropdown.module.css';

const formatSelectedLocation = ({
  uid,
  name,
  grand_opening,
  machine_type,
  soft_opening,
} = {}) => ({
  uid,
  name,
  grand_opening,
  machine_type,
  soft_opening,
});

const LocationsDropdown = ({
  name: inputName,
  selected,
  onChange,
  onBlur,
  error,
}) => {
  const { t } = useTranslation(['common', 'checkout', 'account']);
  const { isSingle, couponCode } = useSelector((state) => state.checkout);
  const { fetchUserPosition } = useUserPosition(false);

  const { query, locale } = useRouter();
  const { format } = useDateTime();
  const fetchPosition = (e) => {
    if (e) e.preventDefault();

    return fetchUserPosition();
  };

  const hasLocationsOrdered = useRef(false);
  const didSelectClosestLocation = useRef(false);

  const locations = useProvideLocations(true, Boolean(query?.s) || couponCode);

  const onSelectedLocation = useCallback(
    (location, event) => {
      onChange(location?.uid ? formatSelectedLocation(location) : null);
      if (event) onBlur(event);
    },
    [onBlur, onChange]
  );

  // If locations only have one location set location
  useEffect(() => {
    if (locations.length === 1 && !selected) {
      onSelectedLocation(locations[0]);
    }
  }, [locations, onSelectedLocation, selected]);

  const {
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
  } = useAutocomplete({
    options: locations,
    getOptionLabel: ({ uid, name, soft_opening }) => {
      if (uid) {
        if (!hasOpened(soft_opening)) {
          return `${formatName(
            name,
            t('openingSoon', {
              ns: 'common',
            })
          )} (${t('doesOpen', {
            ns: 'common',
          })} ${format(soft_opening, 'P')})`;
        }
        return formatName(name);
      }
      return '';
    },
    isOptionEqualToValue: (option, v) => option.uid === v?.uid || true,
    onChange: (e, location) => {
      onSelectedLocation(location, e);
    },
    onClose: (e) => {
      onBlur(e);
    },
    value: selected || null,
  });

  const onClickFetchPosition = () => {
    hasLocationsOrdered.current = true;
    didSelectClosestLocation.current = false;

    fetchPosition().then(() => {
      didSelectClosestLocation.current = false;
    });
  };

  useEffect(() => {
    if (hasLocationsOrdered.current && !didSelectClosestLocation.current) {
      onSelectedLocation(locations[0]);
      didSelectClosestLocation.current = true;
    }
  }, [
    locations,
    hasLocationsOrdered,
    didSelectClosestLocation,
    onSelectedLocation,
  ]);

  const label = (
    <div className={LabelContainer}>
      <div className={LabelText}>{t('locationLabel', { ns: 'checkout' })}</div>
      {locale !== 'de' && (
        <>
          <div className={LabelButton}>
            <Button
              appearance="text"
              size="small"
              onClick={onClickFetchPosition}
              type="button"
            >
              {t('nearestLocation', { ns: 'checkout' })}
            </Button>
            <div className={LabelIcon}>
              <Image
                src="/assets/icons/location-pointer.svg"
                layout="fill"
                alt="pointer"
                blurDataURL="blur"
                placeholder="blur"
                quality={50}
                priority
              />
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={DropdownWrapper}>
      <div {...getRootProps()}>
        <Input
          {...getInputProps()}
          onBlur={(e) => {
            getInputProps().onBlur(e);
            onBlur(e);
          }}
          onKeyDown={(e) => {
            if (e.keyCode === 13) {
              e.target.blur();
            }
          }}
          type="text"
          name={inputName}
          label={label}
          placeholder={t('choosePrimaryWashHall', { ns: 'common' })}
          error={error}
          append={({ isValid }) => (
            <FontAwesomeIcon
              className={classNames(MarkerIconClass, {
                [InputHasIcon]: isValid,
              })}
              icon={faChevronDown}
              color="#06c167"
            />
          )}
          fullWidth
          showValidIcon
        />
      </div>
      {groupedOptions.length > 0 ? (
        <ul className={classNames(Root, Listbox)} {...getListboxProps()}>
          {groupedOptions.map((location, index) => (
            <li
              {...getOptionProps({
                option: location,
                index,
              })}
            >
              {formatName(
                location.name,
                t('openingSoon', {
                  ns: 'common',
                })
              )}
              {!hasOpened(location.soft_opening) &&
                ` (${t('doesOpen', {
                  ns: 'common',
                })} ${format(location.soft_opening, 'P')})`}
            </li>
          ))}
        </ul>
      ) : null}
      {isSingle && (
        <p className={SingleWash}>{t('singleWashInfo3', { ns: 'checkout' })}</p>
      )}
    </div>
  );
};

export default memo(
  LocationsDropdown,
  (prevProps, nextProps) =>
    prevProps.selected === nextProps.selected &&
    JSON.stringify(prevProps.error) === JSON.stringify(nextProps.error) &&
    prevProps.item === nextProps.item
);
